import type { IdTokenManager } from '@ams/shared/auth';
import {
  InsufficientPrivilegeError,
  InvalidRequestError,
  UnauthenticatedError,
  InternalError,
  InvalidSheetsError,
} from './errors.js';
import type {
  InventoryWriteRequest,
  InventoryWriteResponse,
  SheetNameRequest,
  SheetNameResponse,
  ItemSearchRequest,
  ItemSearchResponse,
  ValidateSheetRequest,
  ValidateSheetResponse,
  LocationInventory,
  MasterInventoryItem,
  CheckInRecord,
  CheckOutRecord,
} from '../types.js';

export interface ExpoAmsClient {
  getSheetNames(req: SheetNameRequest): Promise<SheetNameResponse>;
  validateSheet(req: ValidateSheetRequest): Promise<ValidateSheetResponse>;
  getLocationInventory(req: { sheetId: string }): Promise<{ locations: LocationInventory[] }>;
  getMasterInventory(req: { sheetId: string }): Promise<{ items: MasterInventoryItem[] }>;
  getHistory(req: {
    sheetId: string;
  }): Promise<{ checkIns: CheckInRecord[]; checkOuts: CheckOutRecord[] }>;
  searchItem(req: ItemSearchRequest): Promise<ItemSearchResponse>;
  checkIn(req: InventoryWriteRequest): Promise<InventoryWriteResponse>;
  checkOut(req: InventoryWriteRequest): Promise<InventoryWriteResponse>;
  checkInReserved(req: InventoryWriteRequest): Promise<InventoryWriteResponse>;
  checkOutReserved(req: InventoryWriteRequest): Promise<InventoryWriteResponse>;
  getSheetConfig(): Promise<{
    sheetIds: string[];
    /**
     * sheetId -> spreadsheet title, served from the config doc so the
     * bootstrap needs no Sheets API calls. Optional because a frontend
     * build can go live before the functions deploy that adds it; the
     * router falls back to `getSheetNames` for any id missing here.
     */
    sheetNames?: Record<string, string>;
    updatedAt: string | null;
    updatedBy: string | null;
  }>;
  updateSheetConfig(body: { sheetIds: string[] }): Promise<{
    sheetIds: string[];
    updatedAt: string;
    updatedBy: string;
    resolvedNames: Record<string, string>;
  }>;
}

export interface ExpoAmsClientDeps {
  fetch?: typeof globalThis.fetch;
  idTokenManager: IdTokenManager;
  functionsBase: string;
}

const ENDPOINT_MAP = {
  getSheetNames: 'expoamsGetSheetNames',
  validateSheet: 'expoamsValidateSheet',
  getLocationInventory: 'expoamsGetLocationInventory',
  getMasterInventory: 'expoamsGetMasterInventory',
  getHistory: 'expoamsGetHistory',
  searchItem: 'expoamsSearchItem',
  checkIn: 'expoamsCheckIn',
  checkOut: 'expoamsCheckOut',
  checkInReserved: 'expoamsCheckInReserved',
  checkOutReserved: 'expoamsCheckOutReserved',
  getSheetConfig: 'expoamsGetSheetConfig',
  updateSheetConfig: 'expoamsUpdateSheetConfig',
} as const;

export function createExpoAmsClient(deps: ExpoAmsClientDeps): ExpoAmsClient {
  const fetchFn = deps.fetch ?? globalThis.fetch.bind(globalThis);

  async function call<T>(endpoint: string, body: unknown, isRetry = false): Promise<T> {
    const idToken = await deps.idTokenManager.getIdToken();
    const res = await fetchFn(`${deps.functionsBase}/${endpoint}`, {
      method: 'POST',
      // Bearer auth — no cookies needed. See commercial-client.ts for rationale.
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
    });
    if (res.ok) return (await res.json()) as T;
    const errBody = (await res.json().catch(() => ({}))) as { error?: string };
    const msg = errBody.error ?? 'unknown';
    if (res.status === 401) {
      if (msg === 'invalid or expired token' && !isRetry) {
        deps.idTokenManager.invalidate();
        return call<T>(endpoint, body, true);
      }
      throw new UnauthenticatedError(msg);
    }
    if (res.status === 403) throw new InsufficientPrivilegeError(msg);
    if (res.status === 400) {
      // Plan 10: structured invalid_sheets → InvalidSheetsError
      const structured = errBody as { error?: string; results?: unknown };
      if (structured.error === 'invalid_sheets' && Array.isArray(structured.results)) {
        throw new InvalidSheetsError(
          msg,
          structured.results as Array<{
            sheetId: string;
            ok: boolean;
            reason?: string;
            displayName?: string;
          }>,
        );
      }
      throw new InvalidRequestError(msg);
    }
    throw new InternalError(`${res.status}: ${msg}`);
  }

  return {
    getSheetNames: (r) => call(ENDPOINT_MAP.getSheetNames, r),
    validateSheet: (r) => call(ENDPOINT_MAP.validateSheet, r),
    getLocationInventory: (r) => call(ENDPOINT_MAP.getLocationInventory, r),
    getMasterInventory: (r) => call(ENDPOINT_MAP.getMasterInventory, r),
    getHistory: (r) => call(ENDPOINT_MAP.getHistory, r),
    searchItem: (r) => call(ENDPOINT_MAP.searchItem, r),
    checkIn: (r) => call(ENDPOINT_MAP.checkIn, r),
    checkOut: (r) => call(ENDPOINT_MAP.checkOut, r),
    checkInReserved: (r) => call(ENDPOINT_MAP.checkInReserved, r),
    checkOutReserved: (r) => call(ENDPOINT_MAP.checkOutReserved, r),
    getSheetConfig: () => call(ENDPOINT_MAP.getSheetConfig, {}),
    updateSheetConfig: (body) => call(ENDPOINT_MAP.updateSheetConfig, body),
  };
}
